import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import AiManagementModalHeader from 'components/molecules/ai_management_modal_header';
import { applyCompanyAction } from 'services/actions/monitoring_company_actions';
import { apiErrorMessage } from 'services/ensure_ok';
import {
  availableCompanies,
  CompanyAction,
  companiesToChange,
  companyUnavailable,
  initiallyChecked,
  toggleAllCompanies,
} from 'services/monitoring_companies';
import { ApplicationState } from 'store';
import { MonitoredByCompanyTypes } from 'store/modules/monitoring_store/types';
import { ProfileState } from 'store/modules/profile_store/types';
import { Container } from './styles';

const TEXTS: Record<
  CompanyAction,
  { title: string; question: string; button: string; on?: string; off?: string }
> = {
  archive: {
    title: 'Arquivar ou desarquivar',
    question: 'Em quais empresas este aviso fica arquivado?',
    button: 'Salvar',
    on: 'Arquivado',
    off: 'Não arquivado',
  },
  important: {
    title: 'Importante',
    question: 'Em quais empresas este aviso fica marcado como importante?',
    button: 'Salvar',
    on: 'Importante',
    off: 'Não marcado como importante',
  },
  unmonitor: {
    title: 'Desativar monitoramento',
    question: 'Em quais empresas você quer desativar o monitoramento deste aviso?',
    button: 'Desativar',
  },
};

const BLOCKED_TEXT = 'Indisponível nesta empresa';

interface MonitoringCompanyActionModalProps {
  action: CompanyAction | null;
  biddingId: number;
  edital: string | undefined;
  companies: MonitoredByCompanyTypes[] | undefined;
  onClose: () => void;
  onDone: () => void;
}

const MonitoringCompanyActionModal = ({
  action,
  biddingId,
  edital,
  companies,
  onClose,
  onDone,
}: MonitoringCompanyActionModalProps) => {
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const [checked, setChecked] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (action) setChecked(initiallyChecked(action, companies));
    setError('');
  }, [action]);

  if (!action) return null;

  const texts = TEXTS[action];
  const changes = companiesToChange(action, companies, checked);
  const available = availableCompanies(action, companies);
  const allChecked = available.length > 0 && available.every(({ id }) => checked.includes(id));
  const nameOf = (id: number) => companies?.find((company) => company.id === id)?.nome || '';

  const toggle = (id: number) =>
    setChecked((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    );

  const save = async () => {
    setBusy(true);
    setError('');
    const failures = await Promise.all(
      changes.map((id) =>
        applyCompanyAction(action, biddingId, id, profile.id)
          .then(() => null)
          .catch((err) => `${nameOf(id)} (${apiErrorMessage(err, 'tente de novo')})`),
      ),
    );
    setBusy(false);
    onDone();

    const failed = failures.filter(Boolean);
    if (failed.length > 0) setError(`Não foi possível em: ${failed.join(', ')}.`);
    else onClose();
  };

  const situation = (company: MonitoredByCompanyTypes) => {
    const unavailable = companyUnavailable(action, company);
    if (unavailable) return unavailable === company.bloqueio ? BLOCKED_TEXT : unavailable;
    if (action === 'archive') return company.arquivado ? texts.on : texts.off;
    if (action === 'important') return company.importante ? texts.on : texts.off;
    return '';
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <span onClick={(event) => event.stopPropagation()}>
      <Modal show onHide={busy ? () => undefined : onClose} centered style={{ zIndex: 3000000005 }}>
        <Container>
          <AiManagementModalHeader title={texts.title} onClose={onClose} />
          {error && <div className="alert alert-warning mx-4 mt-3 mb-0">{error}</div>}
          <div className="company-action-body px-4 pt-4">
            <p className="mb-3">
              {edital && <strong>{edital}: </strong>}
              {texts.question}
            </p>
            {available.length > 1 && (
              <label
                htmlFor={`company-action-${biddingId}-todas`}
                className="company-row d-flex align-items-start mb-2 pb-2 border-bottom"
              >
                <input
                  id={`company-action-${biddingId}-todas`}
                  type="checkbox"
                  className="mr-2 mt-1"
                  checked={allChecked}
                  disabled={busy}
                  onChange={() => setChecked(toggleAllCompanies(action, companies, checked))}
                />
                <span className="font-weight-bold">Todas as empresas</span>
              </label>
            )}
            {(companies || []).map((company) => {
              const unavailable = Boolean(companyUnavailable(action, company));
              const inputId = `company-action-${biddingId}-${company.id}`;

              return (
                <label
                  key={company.id}
                  htmlFor={inputId}
                  className={`company-row d-flex align-items-start mb-2 ${
                    unavailable ? 'unavailable' : ''
                  }`}
                >
                  <input
                    id={inputId}
                    type="checkbox"
                    className="mr-2 mt-1"
                    checked={checked.includes(company.id)}
                    disabled={busy || unavailable}
                    onChange={() => toggle(company.id)}
                  />
                  <span>
                    {company.nome}
                    <small className="d-block text-muted">{situation(company)}</small>
                  </span>
                </label>
              );
            })}
          </div>
          <div className="d-flex px-4 py-4">
            <button
              type="button"
              className="btn btn-outline-secondary mr-2"
              disabled={busy}
              onClick={onClose}
            >
              Cancelar
            </button>
            <button
              type="button"
              className={`btn ${action === 'unmonitor' ? 'btn-danger' : 'btn-primary'}`}
              disabled={busy || changes.length === 0}
              onClick={save}
            >
              {texts.button}
            </button>
          </div>
        </Container>
      </Modal>
    </span>
  );
};

export default MonitoringCompanyActionModal;
